import { WeeklyPlan, WeeklyAllocation } from '../types';

/**
 * Campos de estado de ejecución de una asignación semanal.
 */
export const MANUAL_STATUS_FIELDS: (keyof WeeklyAllocation)[] = [
    'executed',
    'percentCumplimiento',
    'cncId',
    'comentario',
];

const allocKey = (a: WeeklyAllocation) => `${a.taskId}__${a.unitId}`;

/**
 * Obtiene la marca de tiempo más representativa de una asignación.
 * Prefiere `updatedAt`, luego `statusUpdatedAt`. Si no tiene ninguna, retorna 0.
 */
export const getAllocTimestamp = (a?: WeeklyAllocation): number => {
    if (!a) return 0;
    const tUpdated = a.updatedAt ? Date.parse(a.updatedAt) : NaN;
    const tStatus = a.statusUpdatedAt ? Date.parse(a.statusUpdatedAt) : NaN;
    if (!isNaN(tUpdated) && !isNaN(tStatus)) return Math.max(tUpdated, tStatus);
    if (!isNaN(tUpdated)) return tUpdated;
    if (!isNaN(tStatus)) return tStatus;
    return 0;
};

/**
 * Sello de estado manual específico (para campos de ejecución).
 */
export const getStatusTimestamp = (a?: WeeklyAllocation): number => {
    if (!a) return 0;
    const t = a.statusUpdatedAt ? Date.parse(a.statusUpdatedAt) : NaN;
    if (!isNaN(t)) return t;
    return getAllocTimestamp(a);
};

/**
 * Combina el estado local con el remoto (servidor).
 * Reglas fundamentales para evitar pérdida de datos:
 * 1. Para cada plan semanal existente, se procesan todas las asignaciones existentes (tanto locales como remotas).
 * 2. Si una asignación existe en ambos (local y remoto):
 *    - Si la versión remota tiene un sello más reciente (getAllocTimestamp), se priorizan los datos remotos (responsable, días, etc.).
 *    - Si los campos de estado (executed, etc.) tienen un sello manual remoto más reciente, se toman los campos remotos.
 *    - Si la versión remota tiene estados modificados (ej: ejecutado !== null) y la versión local no tiene timestamp o está pendiente sin timestamp, SE CONSERVA LO REMOTO.
 * 3. Si una asignación existe en el remoto pero no en el local, SE PRESERVA (para no borrar tareas agregadas por otros usuarios/dispositivos).
 * 4. Los planes creados en el servidor que no existan localmente se agregan automáticamente.
 */
export function mergeWeeklyPlans(local: WeeklyPlan[], remote: WeeklyPlan[]): WeeklyPlan[] {
    if (!Array.isArray(local) || local.length === 0) return Array.isArray(remote) ? remote : [];
    if (!Array.isArray(remote) || remote.length === 0) return local;

    const remotePlansMap = new Map<number, WeeklyPlan>();
    remote.forEach(p => p && remotePlansMap.set(p.id, p));

    const mergedPlans: WeeklyPlan[] = [];
    const processedPlanIds = new Set<number>();

    // 1. Procesar planes locales (combinándolos con su par remoto si existe)
    for (const lPlan of local) {
        if (!lPlan) continue;
        processedPlanIds.add(lPlan.id);
        const rPlan = remotePlansMap.get(lPlan.id);

        if (!rPlan || !Array.isArray(rPlan.allocations)) {
            mergedPlans.push(lPlan);
            continue;
        }

        // Combinar asignaciones por clave (taskId + unitId)
        const lAllocsMap = new Map<string, WeeklyAllocation>();
        (lPlan.allocations || []).forEach(a => a && lAllocsMap.set(allocKey(a), a));

        const rAllocsMap = new Map<string, WeeklyAllocation>();
        (rPlan.allocations || []).forEach(a => a && rAllocsMap.set(allocKey(a), a));

        const mergedAllocs: WeeklyAllocation[] = [];
        const processedAllocKeys = new Set<string>();

        // 1a. Iterar asignaciones locales
        for (const [key, lAlloc] of lAllocsMap.entries()) {
            processedAllocKeys.add(key);
            const rAlloc = rAllocsMap.get(key);

            if (!rAlloc) {
                // Solo existe localmente (recién agregada)
                mergedAllocs.push(lAlloc);
                continue;
            }

            const lTime = getAllocTimestamp(lAlloc);
            const rTime = getAllocTimestamp(rAlloc);
            const lStatusTime = getStatusTimestamp(lAlloc);
            const rStatusTime = getStatusTimestamp(rAlloc);

            // Base de la asignación: si la remota es más reciente en estructura, tomamos remota como base
            let base: WeeklyAllocation = { ...(rTime > lTime ? rAlloc : lAlloc) };

            // Protección de RESPONSABLE: Si el remoto tiene timestamp mayor, o si local no tiene timestamp y remoto sí
            if (rAlloc.responsibleId && (rTime > lTime || (!lAlloc.updatedAt && rAlloc.updatedAt))) {
                base.responsibleId = rAlloc.responsibleId;
            }

            // Protección de ESTADO (executed, percentCumplimiento, cncId, comentario):
            // Si el remoto tiene cambio manual más reciente, o si el remoto tiene un estado ejecutado y local está en null sin sello:
            const rHasExecutedState = rAlloc.executed !== null && rAlloc.executed !== undefined;
            const lHasExecutedState = lAlloc.executed !== null && lAlloc.executed !== undefined;

            if (rStatusTime > lStatusTime || (rHasExecutedState && !lHasExecutedState && lStatusTime === 0)) {
                MANUAL_STATUS_FIELDS.forEach(f => {
                    (base as any)[f] = (rAlloc as any)[f];
                });
                base.statusUpdatedAt = rAlloc.statusUpdatedAt || rAlloc.updatedAt;
                base.statusUpdatedBy = rAlloc.statusUpdatedBy || rAlloc.updatedBy;
            }

            // Preservar la marca de tiempo más alta en la asignación combinada
            const finalTime = Math.max(lTime, rTime, lStatusTime, rStatusTime);
            if (finalTime > 0) {
                base.updatedAt = new Date(finalTime).toISOString();
            }

            mergedAllocs.push(base);
        }

        // 1b. Conservar asignaciones que existían en el remoto y no estaban en local
        for (const [key, rAlloc] of rAllocsMap.entries()) {
            if (!processedAllocKeys.has(key)) {
                mergedAllocs.push(rAlloc);
            }
        }

        // El estado del plan (En Planificación / En Ejecución / En Revisión / Cerrada):
        // Prevalece el cambio cronológico más reciente (updatedAt del plan).
        const lPlanTime = lPlan.updatedAt ? Date.parse(lPlan.updatedAt) : 0;
        const rPlanTime = rPlan.updatedAt ? Date.parse(rPlan.updatedAt) : 0;

        let finalStatus = lPlan.status;
        if (lPlanTime > 0 && rPlanTime > 0) {
            if (rPlanTime > lPlanTime) {
                finalStatus = rPlan.status;
            } else {
                finalStatus = lPlan.status;
            }
        } else if (rPlanTime > 0 && lPlanTime === 0) {
            finalStatus = rPlan.status;
        } else if (lPlanTime > 0 && rPlanTime === 0) {
            finalStatus = lPlan.status;
        } else {
            // Si ninguno tiene updatedAt explícito en el plan, el estado local activo prevalece
            finalStatus = lPlan.status || rPlan.status;
        }

        let highestAllocTime = 0;
        mergedAllocs.forEach(a => {
            const t = getAllocTimestamp(a);
            if (t > highestAllocTime) highestAllocTime = t;
        });

        const finalPlanTime = Math.max(lPlanTime, rPlanTime, highestAllocTime);

        mergedPlans.push({
            ...lPlan,
            status: finalStatus,
            allocations: mergedAllocs,
            ...(finalPlanTime > 0 ? { updatedAt: new Date(finalPlanTime).toISOString() } : {})
        });
    }

    // 2. Incorporar planes que existían en el remoto y no en el local
    for (const rPlan of remote) {
        if (rPlan && !processedPlanIds.has(rPlan.id)) {
            mergedPlans.push(rPlan);
        }
    }

    // Ordenar planes por id ascendente
    mergedPlans.sort((a, b) => a.id - b.id);
    return mergedPlans;
}
